import { useEffect, useState } from "react"
import { useParams } from "react-router-dom"
import { AppShell } from "../components/AppShell"
import { useApi } from "../api/hooks"
import { listReceipts, type ReceiptRow } from "../api/receipts"
import { formatEuro } from "../lib/format"
import { useT } from "../lib/i18n"

interface ReceiptWithEntries {
  receipt: ReceiptRow
}

export function ReceiptDetailPage() {
  const { id } = useParams<{ id: string }>()
  const api = useApi()
  const { t } = useT()
  const [data, setData] = useState<ReceiptWithEntries | null>(null)
  const [err, setErr] = useState<string | null>(null)

  useEffect(() => {
    // V1: fetch the receipt by listing then filtering; a dedicated GET /receipts/{id}
    // is Phase 2 work. Entries appear in the diary week endpoint for the receipt's week.
    listReceipts(api).then((r) => {
      const found = r.items.find((it) => it.SK.endsWith(`#${id}`))
      if (!found) {
        setErr(t("not_found"))
      } else {
        setData({ receipt: found })
      }
    }).catch((e: unknown) => setErr(e instanceof Error ? e.message : String(e)))
  }, [api, id, t])

  return (
    <AppShell>
      {err && <p style={{ color: "#ff7a85" }}>{err}</p>}
      {!err && !data && <p style={{ color: "var(--muted)" }}>{t("loading")}</p>}
      {data && (
        <>
          <h1 style={{ margin: 0, fontSize: 28 }}>
            {data.receipt.merchant ?? t("no_merchant_receipt")}
          </h1>
          <dl style={{ marginTop: 16, display: "grid", gridTemplateColumns: "max-content 1fr", gap: 8, color: "var(--muted)" }}>
            <dt>{t("th_date")}</dt><dd style={{ color: "var(--text)" }}>{data.receipt.purchase_date ?? "—"}</dd>
            <dt>{t("th_total")}</dt><dd style={{ color: "var(--text)" }}>
              {data.receipt.total_eur != null ? formatEuro(data.receipt.total_eur) : "—"}
            </dd>
            <dt>{t("th_state")}</dt><dd style={{ color: "var(--text)" }}>{data.receipt.status}</dd>
            <dt>{t("th_lines")}</dt><dd style={{ color: "var(--text)" }}>{data.receipt.lines_count ?? 0}</dd>
            <dt>{t("th_s3_key")}</dt><dd style={{ color: "var(--text)", fontFamily: "monospace", fontSize: 12 }}>{data.receipt.s3_key}</dd>
          </dl>
          <p style={{ marginTop: 24, color: "var(--muted)", fontSize: 13 }}>
            {t("receipt_detail_hint")}
          </p>
        </>
      )}
    </AppShell>
  )
}
